/**
 * useMessageActions Hook
 *
 * Mutation helpers for message actions: read, star, archive, trash, move, bulk.
 * Invalidates relevant queries after each action.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, EmailMessageUpdateInput, EmailBulkActionInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useMessageActions(accountId: string | undefined) {
  const queryClient = useQueryClient();

  /**
   * Narrow invalidation: a read/star/move/archive/trash action only
   * affects messages, threads, and folder unread counts. Invalidating
   * `email.all` was forcing refetch of templates, snippets, signatures,
   * filters, contact-groups, screener, scheduled, snoozed — none of
   * which depend on the action. At scale (mailing-list reader, bulk
   * actions) the cascade was the dominant page-load cost. Per-resource
   * invalidation keeps invalidations tight.
   */
  const invalidateMessageMutationScope = useCallback(() => {
    if (!accountId) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.email.messages(),
      predicate: (q) => q.queryKey[0] === 'email' && q.queryKey[1] === 'messages',
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.email.threads(),
      predicate: (q) => q.queryKey[0] === 'email' && q.queryKey[1] === 'threads',
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.email.folders(accountId) });
  }, [accountId, queryClient]);

  const updateMessage = useCallback(
    async (messageId: string, input: EmailMessageUpdateInput) => {
      if (!accountId) return;
      const result = await api.updateEmailMessage(messageId, accountId, input);
      invalidateMessageMutationScope();
      return result;
    },
    [accountId, invalidateMessageMutationScope],
  );

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!accountId) return;
      await api.deleteEmailMessage(messageId, accountId);
      invalidateMessageMutationScope();
    },
    [accountId, invalidateMessageMutationScope],
  );

  const toggleRead = useCallback(
    async (messageId: string, isRead: boolean) => {
      return updateMessage(messageId, { isRead });
    },
    [updateMessage],
  );

  const toggleStar = useCallback(
    async (messageId: string, isStarred: boolean) => {
      return updateMessage(messageId, { isStarred });
    },
    [updateMessage],
  );

  const moveToFolder = useCallback(
    async (messageId: string, folderId: string) => {
      return updateMessage(messageId, { folderId });
    },
    [updateMessage],
  );

  const bulkAction = useCallback(
    async (
      action: EmailBulkActionInput['action'],
      messageIds: string[],
      extra?: { folderId?: string; labelId?: string },
    ) => {
      if (!accountId || messageIds.length === 0) return;
      const result = await api.bulkEmailMessageAction({
        accountId,
        action,
        messageIds,
        ...extra,
      });
      invalidateMessageMutationScope();
      return result;
    },
    [accountId, invalidateMessageMutationScope],
  );

  const archiveMessages = useCallback(
    (messageIds: string[]) => bulkAction('archive', messageIds),
    [bulkAction],
  );

  const trashMessages = useCallback(
    (messageIds: string[]) => bulkAction('trash', messageIds),
    [bulkAction],
  );

  const markRead = useCallback(
    (messageIds: string[]) => bulkAction('read', messageIds),
    [bulkAction],
  );

  const markUnread = useCallback(
    (messageIds: string[]) => bulkAction('unread', messageIds),
    [bulkAction],
  );

  const starMessages = useCallback(
    (messageIds: string[]) => bulkAction('star', messageIds),
    [bulkAction],
  );

  return {
    updateMessage,
    deleteMessage,
    toggleRead,
    toggleStar,
    moveToFolder,
    bulkAction,
    archiveMessages,
    trashMessages,
    markRead,
    markUnread,
    starMessages,
  };
}
